import React, { useState, useEffect, useMemo, useRef } from "react";
import UpDownArrow from '../assets/ic_updown.svg';
import config from '../config';
import { useNavigate } from "react-router-dom";

const CategoryList = () => {
  const navigate = useNavigate();

  const [categoryList, setCategoryList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [menuOpen, setMenuOpen] = useState(null); // store open menu ID
  const [loading, setLoading] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [addDialog, setAddDialog] = useState({ open: false, title: "", id: null });

  const menuRefs = useRef({});

  useEffect(() => {
    fetchCategories();
  }, []);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuOpen && menuRefs.current[menuOpen] && !menuRefs.current[menuOpen].contains(e.target)) {
        setMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.BASE_URL}/get-category`);
      const data = await res.json();
      if (data.success) setCategoryList(data.data.category);
      else alert("Failed to load categories");
    } catch (err) {
      console.error(err);
      alert("Error fetching categories");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
      key = null;
    }
    setSortConfig({ key, direction });
  };

  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categoryList;
    const lowerSearch = searchTerm.toLowerCase();
    return categoryList.filter(cat => cat.title.toLowerCase().includes(lowerSearch));
  }, [searchTerm, categoryList]);

  const sortedCategories = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredCategories;
    return [...filteredCategories].sort((a, b) => {
      let aVal = a[sortConfig.key].toLowerCase();
      let bVal = b[sortConfig.key].toLowerCase();
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredCategories, sortConfig]);

  const totalEntries = sortedCategories.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  const paginatedCategories = useMemo(() => {
    const start = (currentPage - 1) * entriesPerPage;
    return sortedCategories.slice(start, start + entriesPerPage);
  }, [sortedCategories, currentPage, entriesPerPage]);

  const goToPage = (page) => {
    if (page < 1) page = 1;
    else if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  const saveCategory = async () => {
    if (!addDialog.title.trim()) {
      alert("Title is required");
      return;
    }
    try {
      const url = addDialog.id
        ? `${config.BASE_URL}/update-category/${addDialog.id}`
        : `${config.BASE_URL}/add-category2`;
      const method = addDialog.id ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: addDialog.title }),
      });
      const data = await res.json();
      if (data.success) {
        fetchCategories();
        setAddDialog({ open: false, title: "", id: null });
      } else alert(data.message || "Failed to save category");
    } catch (err) {
      console.error(err);
      alert("Error saving category");
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await fetch(`${config.BASE_URL}/delete-category/${deleteDialog.id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        fetchCategories();
        setDeleteDialog({ open: false, id: null });
      } else alert(data.message || "Failed to delete category");
    } catch (err) {
      console.error(err);
      alert("Error deleting category");
    }
  };

  const formatDate = (dateString) => {
    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return new Date(dateString).toLocaleString("en-GB", options).replace(",", "");
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans">

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="animate-spin h-6 w-6 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <span className="text-gray-800 font-medium">Loading...</span>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="mb-4 flex flex-wrap justify-between items-center gap-3 bg-white rounded-[6px] px-4 py-3">
        <button type="button" onClick={() => setAddDialog({ open: true, title: "", id: null })} className="bg-[#000000] text-white px-4 py-2 rounded hover:bg-[#00000F] transition" >
          Add New Category +
        </button>
        <input
          type="search"
          placeholder="Search"
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto p-3 bg-white rounded-[6px] border border-transparent">
        <table className="w-full text-left text-sm text-gray-700 table-fixed border border-gray-200">
          <thead className="bg-white border-b border-gray-300">
            <tr className="divide-x divide-gray-300">
              <th className="p-4 select-none">
                <div className="inline-flex items-center space-x-2">
                  <span>Category Name</span>
                  <img
                    src={UpDownArrow}
                    alt="Sort"
                    className={`w-4 h-4 cursor-pointer transition-transform ${sortConfig.key === "title" ? (sortConfig.direction === "asc" ? "rotate-180" : "rotate-0") : ""}`}
                    onClick={() => handleSort("title")}
                  />
                </div>
              </th>
              <th className="p-4 select-none text-center">
                <div className="inline-flex items-center justify-center space-x-2">
                  <span>Updated On</span>
                  <img
                    src={UpDownArrow}
                    alt="Sort"
                    className={`w-4 h-4 cursor-pointer transition-transform ${sortConfig.key === "updatedAt" ? (sortConfig.direction === "asc" ? "rotate-180" : "rotate-0") : ""}`}
                    onClick={() => handleSort("updatedAt")}
                  />
                </div>
              </th>
              <th className="p-4 text-center">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {paginatedCategories.length === 0 ? (
              <tr>
                <td colSpan={3} className="p-4 text-center text-gray-500">No categories found.</td>
              </tr>
            ) : (
              paginatedCategories.map((cat, index) => (
                <tr key={cat.id} className={`${index % 2 === 0 ? "bg-gray-100" : "bg-white"} divide-x divide-gray-300`}>
                  <td className="p-4 text-gray-900 font-semibold">{cat.title}</td>
                  <td className="p-4 text-center text-gray-700">{formatDate(cat.updatedAt)}</td>
                  <td className="p-4 text-center">
                    <div className="relative inline-block" ref={el => menuRefs.current[cat.id] = el}>
                      <button
                        onClick={() =>
                          setMenuOpen(prev => prev === cat.id ? null : cat.id)
                        }
                        className="p-1 hover:bg-gray-200 rounded-full"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                          <circle cx="10" cy="3" r="2" />
                          <circle cx="10" cy="10" r="2" />
                          <circle cx="10" cy="17" r="2" />
                        </svg>
                      </button>

                      {menuOpen === cat.id && (
                        <div className="absolute left-full top-0 ml-2 w-32 bg-white border border-gray-300 rounded-md shadow-lg text-sm text-gray-800 z-20">
                          <button
                            onClick={() => {
                              setAddDialog({ open: true, title: cat.title, id: cat.id });
                              setMenuOpen(null);
                            }}
                            className="block w-full text-left px-4 py-2 hover:bg-gray-100 hover:text-blue-600"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setDeleteDialog({ open: true, id: cat.id });
                              setMenuOpen(null);
                            }}
                            className="block w-full text-left px-4 py-2 hover:bg-gray-100 hover:text-red-600"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="mt-4 flex flex-col md:flex-row items-center justify-between text-gray-600 text-sm space-y-2 md:space-y-0">
          <div className="text-black font-bold">
            Showing {totalEntries === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1} to {Math.min(currentPage * entriesPerPage, totalEntries)} of {totalEntries} entries
          </div>

          <div className="flex items-center space-x-1">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2))
              .map((page, i, arr) => {
                if (i > 0 && page - arr[i - 1] > 1) return <span key={page} className="px-3 py-1 text-gray-500 select-none">...</span>;
                return (
                  <button key={page} onClick={() => goToPage(page)} className={`px-3 py-1 rounded ${page === currentPage ? "bg-[#000000] text-white font-semibold" : "hover:bg-gray-200"}`}>{page}</button>
                );
              })}
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Delete Dialog */}
      {deleteDialog.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-800">Delete Category</h2>
            <p className="text-gray-600 mt-2">Are you sure you want to delete this category?</p>
            <div className="mt-4 flex justify-end space-x-3">
              <button onClick={() => setDeleteDialog({ open: false, id: null })} className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      {addDialog.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
            <button onClick={() => setAddDialog({ open: false, title: "", id: null })} className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 font-bold text-lg">&times;</button>
            <h2 className="text-lg font-bold text-gray-800">{addDialog.id ? "Edit Category" : "Add New Category"}</h2>
            <div className="mt-4">
              <label className="block text-gray-700 mb-1">Category Name</label>
              <input
                type="text"
                value={addDialog.title}
                onChange={(e) => setAddDialog(prev => ({ ...prev, title: e.target.value }))}
                className="w-full border border-gray-300 rounded-md p-2"
              />
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={saveCategory} className="px-4 py-2 w-full bg-[#000000] text-white rounded-md hover:bg-[#000000]">{addDialog.id ? "Update" : "Add"}</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CategoryList;
